import React, { useEffect, useState } from 'react';
import { PackageOpen } from 'lucide-react';
import { Product, ProductCardStyle } from '../../types';
import { ShopSection } from '../../services/shop';
import { ProductCard } from './ProductCard';
import { ProductCardSkeleton } from '../ui/Skeleton';

/** Avståndet under butikens fasta sidhuvud och kategoriraden, så att rubriken syns efter ett hopp. */
const SECTION_OFFSET = 'scroll-mt-36';

/**
 * Kategoriraden. Följer med vid scroll (sticky) och hoppar till sektionen,
 * den filtrerar inte. Den sektion som syns markeras.
 */
export const CategoryNav: React.FC<{ sections: ShopSection[]; scrollContainerId?: string }> = ({ sections, scrollContainerId }) => {
    const [active, setActive] = useState<string | null>(sections[0]?.id ?? null);

    useEffect(() => {
        const root = scrollContainerId ? document.getElementById(scrollContainerId) : null;
        const els = sections.map(s => document.getElementById(s.id)).filter((e): e is HTMLElement => !!e);
        if (els.length === 0 || typeof IntersectionObserver === 'undefined') return;
        const io = new IntersectionObserver(entries => {
            const visible = entries.filter(e => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
            if (visible[0]) setActive(visible[0].target.id);
        }, { root, rootMargin: '-140px 0px -55% 0px' });
        els.forEach(el => io.observe(el));
        return () => io.disconnect();
    }, [sections, scrollContainerId]);

    if (sections.length < 2) return null;
    const jump = (id: string) => {
        setActive(id);
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    // Upp till tre kategorier: lika breda knappar. Fler: centrerad rad som scrollar i sidled när den inte får plats.
    const even = sections.length <= 3;
    const chip = (s: ShopSection) => (
        <button
            type="button"
            onClick={() => jump(s.id)}
            aria-current={active === s.id ? 'true' : undefined}
            className={`w-full min-h-[40px] px-4 rounded-full text-sm font-bold whitespace-nowrap transition-colors ${active === s.id ? 'bg-primary text-white' : 'bg-white/80 text-primary border border-black/10 hover:border-primary/40'}`}
        >
            {s.title}
        </button>
    );

    return (
        // Samma bakgrund och blur som butikens sidhuvud (StoreHeader), så att de ser ut som en del.
        <nav
            aria-label="Kategorier"
            className="sticky top-16 z-30"
            style={{ backgroundColor: 'rgba(255, 255, 255, 0.92)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)', borderBottom: '1px solid rgba(0,0,0,0.05)' }}
        >
            {even ? (
                <ul className="max-w-xl mx-auto px-4 py-2.5 grid gap-2 list-none" style={{ gridTemplateColumns: `repeat(${sections.length}, minmax(0, 1fr))` }}>
                    {sections.map(s => <li key={s.id} className="min-w-0">{chip(s)}</li>)}
                </ul>
            ) : (
                <div className="overflow-x-auto no-scrollbar">
                    <ul className="w-max mx-auto px-4 md:px-8 py-2.5 flex gap-2 list-none">
                        {sections.map(s => <li key={s.id} className="flex-shrink-0">{chip(s)}</li>)}
                    </ul>
                </div>
            )}
        </nav>
    );
};

/** Hela sortimentet på en sida, en sektion per kategori. 2 kolumner på mobil, 4 på dator. */
export const ShopCatalog: React.FC<{
    sections: ShopSection[];
    onSelect: (product: Product) => void;
    cardStyle?: ProductCardStyle;
    isLoading?: boolean;
}> = ({ sections, onSelect, cardStyle = 'classic', isLoading }) => {
    if (isLoading) {
        return (
            <div className="max-w-7xl mx-auto px-4 md:px-8 py-12 grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
                {Array.from({ length: 4 }, (_, i) => <ProductCardSkeleton key={i} />)}
            </div>
        );
    }
    if (sections.length === 0) {
        return (
            <div className="max-w-7xl mx-auto px-4 md:px-8 py-16">
                <div className="py-20 bg-white rounded-3xl border border-stone-100 flex flex-col items-center justify-center text-center">
                    <PackageOpen className="text-stone-300 mb-3" size={40} />
                    <h3 className="text-lg font-bold text-stone-700 mb-1">Butiken är tom</h3>
                    <p className="text-stone-400 text-sm">Inga produkter finns tillgängliga just nu.</p>
                </div>
            </div>
        );
    }
    return (
        <div id="products" className="max-w-7xl mx-auto px-4 md:px-8 pt-10 md:pt-14 pb-16 md:pb-24 flex flex-col gap-12 md:gap-16">
            {sections.map(s => (
                <section key={s.id} id={s.id} aria-labelledby={`${s.id}-rubrik`} className={SECTION_OFFSET}>
                    <h2 id={`${s.id}-rubrik`} className="text-2xl md:text-3xl font-heading font-bold text-primary mb-5 md:mb-6">{s.title}</h2>
                    <ul className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 list-none p-0">
                        {s.products.map(p => (
                            <li key={p.id} className="h-full">
                                <ProductCard product={p} onAddToCart={onSelect} cardStyle={cardStyle} />
                            </li>
                        ))}
                    </ul>
                </section>
            ))}
        </div>
    );
};
